import type { Dir, MatchState, Pawn, Side, Square } from './types';

export const BOARD_SIZE = 6;
export const FILES = 'ABCDEF';

export const opponent = (side: Side): Side => (side === 'S' ? 'N' : 'S');

/** Forward sign f (§3.2): +1 for South, −1 for North. */
export const forwardSign = (side: Side): 1 | -1 => (side === 'S' ? 1 : -1);

/** y of a side's own home row. */
export const homeRow = (side: Side): number => (side === 'S' ? 0 : BOARD_SIZE - 1);

/** y of the enemy back row (where a side's pawns crown). */
export const enemyBackRow = (side: Side): number => homeRow(opponent(side));

export const onBoard = (x: number, y: number): boolean =>
  x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE;

export const sq = (x: number, y: number): Square => ({ x, y });

export const sameSquare = (a: Square | null | undefined, b: Square | null | undefined): boolean =>
  !!a && !!b && a.x === b.x && a.y === b.y;

export const squareIndex = (s: Square): number => s.y * BOARD_SIZE + s.x;

export const isSquare = (v: unknown): v is Square => {
  if (typeof v !== 'object' || v === null) return false;
  const s = v as { x?: unknown; y?: unknown };
  return Number.isInteger(s.x) && Number.isInteger(s.y) && onBoard(s.x as number, s.y as number);
};

/** Square name in notation (§3.6), e.g. "C4". */
export const squareName = (s: Square): string => `${FILES[s.x] ?? '?'}${s.y + 1}`;

/** Parses "C4" into a square; returns null when invalid. */
export function parseSquare(name: string): Square | null {
  const m = /^([A-Fa-f])([1-6])$/.exec(name.trim());
  if (!m) return null;
  return { x: FILES.indexOf((m[1] as string).toUpperCase()), y: Number(m[2]) - 1 };
}

/** Parses "C4" or throws; for tests and fixtures. */
export function square(name: string): Square {
  const s = parseSquare(name);
  if (!s) throw new Error(`bad square: ${name}`);
  return s;
}

export const ORTHOGONAL: readonly { dx: number; dy: number }[] = [
  { dx: 0, dy: 1 },
  { dx: 0, dy: -1 },
  { dx: 1, dy: 0 },
  { dx: -1, dy: 0 },
];

export const DIAGONAL: readonly Dir[] = [
  { dx: 1, dy: 1 },
  { dx: -1, dy: 1 },
  { dx: 1, dy: -1 },
  { dx: -1, dy: -1 },
];

export const KNIGHT: readonly { dx: number; dy: number }[] = [
  { dx: 1, dy: 2 },
  { dx: -1, dy: 2 },
  { dx: 2, dy: 1 },
  { dx: -2, dy: 1 },
  { dx: 1, dy: -2 },
  { dx: -1, dy: -2 },
  { dx: 2, dy: -1 },
  { dx: -2, dy: -1 },
];

/** Forward component of a displacement for a side (§3.2). */
export const forwardComponent = (side: Side, dy: number): number => dy * forwardSign(side);

/** Occupancy grid: pawn id per square index, or null. */
export type Occupancy = (string | null)[];

export function occupancy(pawns: Readonly<Record<string, Pawn>>): Occupancy {
  const occ: Occupancy = new Array<string | null>(BOARD_SIZE * BOARD_SIZE).fill(null);
  for (const id in pawns) {
    const p = pawns[id] as Pawn;
    if (p.status === 'onBoard' && p.square) occ[squareIndex(p.square)] = p.id;
  }
  return occ;
}

/** A pawn is immune while it stands on its own home row and the home-row rule is on (§8.7). */
export const isImmune = (state: Pick<MatchState, 'config'>, pawn: Pawn): boolean =>
  state.config.homeRowImmunity &&
  pawn.status === 'onBoard' &&
  pawn.square?.y === homeRow(pawn.side);

export const onBoardPawns = (pawns: Readonly<Record<string, Pawn>>, side: Side): Pawn[] => {
  const out: Pawn[] = [];
  for (const id in pawns) {
    const p = pawns[id] as Pawn;
    if (p.side === side && p.status === 'onBoard') out.push(p);
  }
  return out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
};
